import { Component, OnInit } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ApiService } from 'src/app/api.service';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { AuthenticationService } from 'src/app/_services';
import { SecurityDocument } from 'src/app/shared/models/SecurityDocument';
import { first } from 'rxjs/operators';
import { Constants } from 'src/app/shared/vo/Constants';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TovpilLanguageService } from 'src/app/utils/tovpil-language.service';
import { CountryPrice } from 'src/app/shared/models/CountryPrice';
import { DatePipe } from '@angular/common';
import { CountryVO } from 'src/app/shared/models/CountryVO';
import { TovpilCrudService } from 'src/app/utils/tovpil-crud-service';
import { TovpilUtilsService } from 'src/app/utils/tovpil-utils.service';
import { ValuesVO } from 'src/app/shared/models/ValuesVO';

@Component({
  selector: 'app-price-list',
  templateUrl: './price-list.component.html',
  styleUrls: ['./price-list.component.scss'],
  providers: [MessageService, ConfirmationService, TovpilLanguageService]
})
export class PriceListComponent implements OnInit {

  /** SECURITY */
  currentUser: any;
  security: SecurityDocument;
  token: string;
  device: string;
  storeId: string;
  dataParent: any;
  isButtonDisabled : boolean = false;
  monthsMessage : string;

  /** LABELS */
  translations: Record<string, string> = {};
  constantsLocal = Constants;
  failedMsg: string;

  /** COMPONENT */
  stores: CountryPrice[];
  countrySelected: CountryVO;
  storeSelected: CountryPrice;
  createUpdateDialog: boolean;
  listLanguage: ValuesVO[] = [];
  selectedLanguage: ValuesVO;

  /** COMPONENT EXPORT */
  blocked: boolean = true;
  displayModalPriceList: boolean = false;
  dataForm: FormGroup;

  endPointFindCountryByCurrLanguage = "findByCountryCurrLang";
  endPointGetStoreMaterialsByCountry = "getStoreMaterialsByCountry";
  endPointGetCountryMaterials = "getCountryMaterials";
  endPointListItemsStoreCountry = "listItemsStoreCountry";
  endPointCreateUpdatePriceZoneList = "createUpdatePriceListDetail";

  /**
   * This constructor initializes various services and forms, and authenticates the user if the token and
   * device parameters are valid.
   * @param {MessageService} messageService - The `messageService` is a service used for displaying
   * messages or notifications to the user.
   * @param {ApiService} apiService - An instance of the ApiService class, which is responsible for
   * making API requests and handling responses.
   * @param {ActivatedRoute} route - The `route` parameter is used to access the current route's
   * information, such as the URL parameters and query parameters. It is provided by the Angular Router
   * module and is used to subscribe to changes in the route's query parameters using the `queryParams`
   * property. In the code snippet, it is used
   * @param {AuthenticationService} authenticationService - The `authenticationService` parameter is a
   * service that handles user authentication and authorization. It is used to authenticate the user and
   * retrieve the current user information.
   * @param {FormBuilder} fb - The `fb` parameter is an instance of the `FormBuilder` class. It is used
   * to create instances of `FormControl` and `FormGroup` objects, which are used to build and manage
   * forms in Angular applications.
   * @param {ConfirmationService} confirmationService - The `confirmationService` is a service that
   * provides methods for displaying confirmation dialogs to the user. It is typically used to confirm
   * actions before proceeding, such as deleting an item or submitting a form.
   * @param {TovpilLanguageService} translationService - The `translationService` parameter is an
   * instance of the `TovpilLanguageService` class, which is used for handling translations in the
   * application. It is injected into the constructor using dependency injection.
   * @returns The constructor does not return anything. It is used to initialize the properties and
   * dependencies of the class.
   */
  constructor(
    private messageService: MessageService,
    private apiService: ApiService,
    private route: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private fb: FormBuilder,
    private router: Router,
    private confirmationService: ConfirmationService,
    private translationService: TovpilLanguageService,
    private datePipe: DatePipe,
    private tovpilCrudService: TovpilCrudService,
    private tovpilUtilsService: TovpilUtilsService
  ) {
    // ROUTER PARAMS
    this.route.queryParams.subscribe(params => {
      this.token = params.token;
      this.device = params.device;
      this.storeId = params.storeId;
    });

    const navigation = this.router.getCurrentNavigation();
    this.dataParent = navigation.extras.state as any;

    if (this.dataParent) {
      this.countrySelected = this.dataParent.country;

    } else {
      this.apiService.getCountryById({
        countryId: this.security.region,
      }).then(
        data => {
          this.countrySelected = data;
        }).catch(error => {
          console.error("An error occurred while fetching purchase order data:");
          this.messageService.add({ severity: 'warn', summary: `${this.translations.lbl_city}`, detail: `${this.failedMsg}: ${error}` });
        });
    }

    // FORMS
    this.dataForm = this.fb.group({
      currentList: new FormControl({ value: null, disabled: true }, [
        Validators.maxLength(60),
        Validators.minLength(2),
      ]),
      country: new FormControl({ value: null, disabled: true }, [
        Validators.maxLength(60),
        Validators.minLength(2),
      ]),
      startDate: new FormControl(null, [
        Validators.required,
        Validators.maxLength(60),
        Validators.minLength(2),
      ])
    });

    // INIT AUTH
    if (!this.token || this.device !== Constants.APP_USA || this.device !== Constants.APP_DEVICE_ID) {
      this.currentUser = this.authenticationService.currentUserValue;
      return;
    }

    this.authenticateByZuul('generalaccess', 'hY6Mn_4hn9vDrWrh6');
  }

  /**
   * The ngOnInit function initializes the component by setting the currentUser property to the current
   * user value from the authentication service and calling the getSecurity function.
   */
  ngOnInit(): void {
    this.currentUser = this.authenticationService.currentUserValue;
    if (this.dataParent.selectedLanguage) {
      this.selectedLanguage = this.dataParent.selectedLanguage;
    }
    this.getSecurity();
  }

  /**
   * The function `authenticateByZuul` authenticates a user by calling the `login` method of the
   * `authenticationService` and performs certain actions based on the response data.
   * @param {string} user - A string representing the username or user ID of the user trying to
   * authenticate.
   * @param {string} password - The password parameter is a string that represents the user's password.
   */
  authenticateByZuul(user: string, password: string) {
    this.authenticationService.login(user, password)
      .pipe(first())
      .subscribe(
        data => {
          if (data.code === 'R-0200') {
            this.getSecurity();
          } else {
            // this.messageService.add({ severity: 'success', summary: 'Autenticación', detail: Constants.SUCCESS_MESSAGE });
          }
        },
        error => {
          this.messageService.add({ severity: 'warn', summary: 'Authentication', detail: `Error: ${this.translations.lbl_login}, ${this.translations.lbl_then_try_it_again}`});
        }
      );
  }

  /**
   * The function `getSecurity()` retrieves security data from an API service and then loads the
   * translated labels.
   */
  getSecurity() {
    if (!this.security) {
      this.apiService.findSecurityById({ "id": this.token ?? this.currentUser?.tokenProfile }).then(
        data => {
          this.getPropertiesValue();
          this.security = data;
          this.load();
        }
      );
    } else {
      this.getTranslatedLabels();
    }
  }

  async getPropertiesValue(){
    let res :string = await this.apiService.getPropertyValue({
      "id" : "pricelist.available.months"
    });
    
    const month = new Date().getMonth() + 1;
    const monthString = month.toString().padStart(2, '0');
    if(res.includes(monthString)){
      this.isButtonDisabled = false;
    }else{
      this.isButtonDisabled = true;
    }
    let months = this.obtainMonthsNames(res, this.security.language);
    this.monthsMessage = 'La lista de precios solo puede ser modificada en los meses: ' + months;
    console.log(this.monthsMessage);
  }

  /**
   * The goBack function closes the current window.
   */
  goBack() {
    try {
      let navigationExtras: NavigationExtras = {};

      try {
        this.router.navigate(['/treasurer/priceCountry'], navigationExtras);
      } catch (navigationError) {
        console.error("An error occurred while navigating to coordination catalog details:", navigationError);
        this.messageService.add({ severity: 'warn', summary: `${this.translations.lbl_list} ${this.translations.lbl_of} ${this.translations.lbl_city}`, detail: `Error: ${navigationError}`, life: 5000 });
      }
    } catch (parseError) {
      console.error("An error occurred while parsing state to JSON:", parseError);
      this.messageService.add({ severity: 'warn', summary:  `${this.translations.lbl_list} ${this.translations.lbl_of} ${this.translations.lbl_city}`, detail: `Error: ${parseError}`, life: 5000 });
    }
  }

  /**
   * The function retrieves translated labels using the language and region specified in the security
   * object.
   */
  getTranslatedLabels() {
    this.translationService.getTranslatedLabels(this.security.language, this.security.region).then(() => {
      this.translations = this.translationService.getLabels();
      this.failedMsg = this.translations.lbl_internal_server_error;
    });
  }

  /**
   * The `load` function is responsible for loading data and translated labels.
   */
  private load() {
    //console.debug("Loading ...")
    this.getTranslatedLabels();
    this.getCountrySpkLangByCountryId();
  }

  /**
   * The `getData()` function fetches a list of dependent local stores from an API service, displays a
   * success message if the data is retrieved successfully, displays a warning message if there is an
   * error, and sets the `blocked` property to false after a delay of 1500 milliseconds.
   */
  getData() {
    this.blocked = true;
    this.apiService.getCountryPriceList({
      countryId: this.dataParent.country.countryId ?? null,
      language: this.selectedLanguage.valueId != null ? this.selectedLanguage.valueId : this.security.language
    }).then(
      data => {
        this.stores = data;
        this.storeSelected = this.stores.find(item => item.selected === true);
        this.transformDataValues(this.stores);
        this.setDefaultValuesForm(this.security);
        // this.messageService.add({ severity: 'success', summary: 'Lista de Ciudad', detail: Constants.SUCCESS_MESSAGE });
      }).catch(error => {
        console.error("An error occurred while fetching purchase order data:");
        this.messageService.add({ severity: 'warn', summary: `${this.translations.lbl_list} ${this.translations.lbl_of} ${this.translations.lbl_city}`, detail: `Error: ${error}` });
      }).finally(() => {
        setTimeout(() => {
          this.blocked = false;
        }, 1500);
      });
  }

  /**
   * The function "openCreatePriceList" sets the "displayModalPriceList" variable to true, which opens a
   * modal for creating a price list.
   */
  openCreatePriceList(order: any) {
    this.displayModalPriceList = true;
    this.createUpdateDialog = order === null ? true : false;
  }

  /**
   * The function "createPriceList" is used to create a price list and display a confirmation message
   * before saving the list.
   */
  createUpdatePriceList() {
    //console.debug("Creando ...");
    let params = {
      "currentListValue": this.dataForm.get('currentList').value, // ESTABLCER DE LO EXISTENTE
      "countryValue": this.dataForm.get('country').value,
      "startDateValue": this.dataForm.get('startDate').value, // ESTABLCER DE LO EXISTENTE
      "scopeValue": "N"
    };

    this.displayModalPriceList = false;

    if (this.dataForm.valid) {
      this.confirmationService.confirm({
        message: `¿${this.translations.lbl_are_you_sure}?`,
        header: this.translations.lbl_confirm,
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translations.lbl_yes,
        rejectLabel: this.translations.lbl_back,
        accept: () => {
          if (this.createUpdateDialog) {
            this.createRecord();
          } else {
            this.updateRecord();
          }
        },
        reject: () => {
          this.messageService.add({ severity: 'warn', summary: `${this.translations.lbl_price_list}`, detail: `Error: ${this.failedMsg}` });
        }
      });
    } else {
      // Error faltan datos en el formulario
      this.messageService.add({ severity: 'warn', summary: `${this.translations.lbl_price_list}`, detail: `Error: ${this.translations.lbl_price_list} ${this.translations.lbl_is_not_valid}` });
    }

  }

  /* The above code is defining an asynchronous function called `updateRecord` that takes in a parameter
  `params`. Inside the function, it awaits the result of a `validator` function and assigns it to the
  variable `responseValidator`. If `responseValidator` is falsy, it enters a try-catch block. Inside
  the try block, it logs a debug message "Create: " followed by the value of `params`. It then resets
  a form called `dataForm` and sets default values for a form using a variable `security`. If there is
  an error, it logs the error. If ` */
  async createRecord() {
    const responseValidator = await this.validator();
    if (responseValidator) {
      //console.debug("Validador: ", responseValidator);
      return;
    }

    try {
      this.blocked = true;
      const param = {
        "storeId": this.security.storeId ?? null,
        "language": this.security.language ?? null
      };

      const [resultMaterials, resultCountry] = await Promise.all([
        this.tovpilCrudService.fetch(this.endPointGetStoreMaterialsByCountry, param),
        this.tovpilCrudService.fetch(this.endPointGetCountryMaterials, param)
      ]);

      const [resultItem] = await Promise.all([
        this.tovpilCrudService.fetch(this.endPointListItemsStoreCountry, {
          "storeId": resultMaterials.data.storeId,
          "countryId": resultCountry.data.countryId,
          "language": this.security.language,
          "lstInclude": null,
          "lstExclude": null,
          "sellLPC": false,
          "sellSPG": false,
          "sellGUI": false,
          "sellPUB": false,
          "onlyInvent": true
        })
      ]);

      if (resultMaterials.success && resultCountry.success && resultItem.success) {
        const paramToCreateUpdate = {
          "currPriceL": this.storeSelected.listId,
          "newListId": null,
          "date": this.tovpilUtilsService.convertDateFormat(this.dataForm.get('startDate').value),
          "listScope": "N",
          "storeMater": resultMaterials.data.storeId,
          "countryMater": resultCountry.data.countryId,
          "listItem": resultItem.data
        };

        //console.debug("Create: ", paramToCreateUpdate);

        const resultClone = await this.tovpilCrudService.fetch(this.endPointCreateUpdatePriceZoneList, paramToCreateUpdate);
        const severity = resultClone.success ? 'success' : 'warn';
        const detailMessage = resultClone.success ? Constants.SUCCESS_MESSAGE : Constants.FAILED_MESSAGE;
        if(severity == 'warn'){
          this.messageService.add({ severity, summary: `${this.translations.lbl_update} ${this.translations.lbl_list} ${this.translations.lbl_zonal}`, detail: this.failedMsg, life: 3000 });
        }
        

        this.dataForm.reset();
        this.setDefaultValuesForm(this.security);
        this.load();
      } else {
        console.error("An error occurred while fetching data:", resultMaterials);
        this.messageService.add({ severity: 'warn', summary: `${this.translations.lbl_update} ${this.translations.lbl_list} ${this.translations.lbl_zonal}`, detail: `Error: ${this.translations.lbl_search} ${this.translations.lbl_list}` });
      }

      this.dataForm.reset();
      this.setDefaultValuesForm(this.security);
      this.load();
      this.blocked = false;

    } catch (error) {
      console.error("An error occurred:", error);
      this.blocked = false;
    }
  }

  /**
   * The `updateRecord` function is an asynchronous function that updates a record by making an API call
   * and displaying a success or failure message.
   * @returns The function does not explicitly return anything.
   */
  async updateRecord() {
    const responseValidator = await this.validator();

    if (responseValidator) {
      //console.debug("Validador: ", responseValidator);
      return;
    }
    try {
      this.blocked = true;
      const paramToCreateUpdate = {
        "currPriceL": this.storeSelected.listId,
        "newListId": this.storeSelected.listId,
        "date": this.tovpilUtilsService.convertDateFormat(this.dataForm.get('startDate').value),
        "listScope": "N",
        "storeMater": null,
        "countryMater": null,
        "listItem": null
      };

      //console.debug("Update: ", paramToCreateUpdate);
      const resultClone = await this.tovpilCrudService.fetch(this.endPointCreateUpdatePriceZoneList, paramToCreateUpdate);
      const severity = resultClone.success ? 'success' : 'warn';
      const detailMessage = resultClone.success ? Constants.SUCCESS_MESSAGE : Constants.FAILED_MESSAGE;

      if(severity == 'warn'){
        this.messageService.add({ severity, summary:  `${this.translations.lbl_update} ${this.translations.lbl_list} ${this.translations.lbl_zonal}`, detail: this.failedMsg, life: 3000 });
      }
      
      this.dataForm.reset();
      this.setDefaultValuesForm(this.security);
      this.load();
      this.blocked = false;
    } catch (error) {
      console.error("An error occurred:", error);
      this.blocked = false;
    }
  }

  /**
   * The `validator` function is an asynchronous function that fetches price list data and checks for
   * certain conditions, returning a boolean result.
   * @returns a boolean value, which indicates whether there was an error or not during the validation
   * process.
   */
  async validator() {
    let result = false;
    let lastDate = null;
    let msg = "";
    try {
      const resultCurrent = await this.tovpilCrudService.fetch(this.endPointFindCountryByCurrLanguage, {
        "countryId": this.storeSelected.countryId ?? null,
        "currency": this.storeSelected.currCode ?? null,
        "language": this.security.language ?? null
      });

      if (resultCurrent.success) {
        let data = resultCurrent.data;
        data.forEach(element => {

          if (element['status'] === Constants.NEW_STATUS && !(element['listId'] === this.storeSelected.listId)) {
            msg += "Usted ya cuenta con una lista en analisis";
            result = true;
          }

          if (!(element['listId'] === this.storeSelected.listId)) {
            lastDate = element['currDate'];
          }
        });

        if (lastDate != null && lastDate != undefined && (parseFloat(this.tovpilUtilsService.convertDateFormat(this.dataForm.get('startDate').value)) < parseFloat(lastDate))) {
          msg += "__ Fecha de vigencia no puede ser menor al de una lista previa";
          result = true;
        }

        if (result) {
          this.messageService.add({ severity: 'warn', summary: this.translations.lbl_price_list, detail: `Error: ${this.translations.lbl_search} ${this.translations.lbl_price_list}`});
        }
      } else {
        console.error("An error occurred while fetching price list:", resultCurrent);
        this.messageService.add({ severity: 'warn', summary: this.translations.lbl_price_list, detail: `Error: ${this.translations.lbl_search} ${this.translations.lbl_price_list}` });
      }

    } catch (error) {
      console.error("An error occurred while fetching price list:", error);
      this.messageService.add({ severity: 'warn', summary: `${this.translations.lbl_price_list} ${this.translations.lbl_zonal}`, detail: `Error: ${this.translations.lbl_search} ${this.translations.lbl_price_list} ${this.translations.lbl_zonal}` });
    }
    return result;
  }

  /**
   * The function `redirectToPriceListByList` redirects the user to a price list based on their security
   * level.
   */
  async redirectToPriceListByList(_model: CountryPrice) {
    try {

      let navigationExtras: NavigationExtras = {
        state: JSON.parse(JSON.stringify({
          security: this.security,
          storeParams: _model,
          country: this.dataParent.country,
          selectedLanguage: this.selectedLanguage
        }))
      };
      let route = this.security.levelId == 'I' ? '/treasurer/priceListInt' : '/treasurer/priceListZon';

      try {
        this.router.navigate([route], navigationExtras);
      } catch (navigationError) {
        console.error("An error occurred while navigating to coordination catalog details:", navigationError);
        this.messageService.add({ severity: 'warn', summary: `${this.translations.lbl_list} ${this.translations.lbl_of} ${this.translations.lbl_city}`, detail: `${this.failedMsg}: ${navigationError}`, life: 5000 });
      }
    } catch (parseError) {
      console.error("An error occurred while parsing state to JSON:", parseError);
      this.messageService.add({ severity: 'warn', summary: `${this.translations.lbl_list} ${this.translations.lbl_of} ${this.translations.lbl_city}`, detail: `${this.failedMsg}: ${parseError}`, life: 5000 });
    }
  }

  /**
   * The function `transformDataValues` updates the `listScopeDescrip` property of each item in the `itms`
   * array by calling the `getListScope` function and assigns the updated array to the `stores` property.
   * @param {CountryPrice[]} itms - An array of objects representing country prices. Each object has
   * properties such as "listScope" and "listScopeDescrip".
   */
  async transformDataValues(itms: CountryPrice[]) {
    for (const itm of itms) {
      if (itm.listScope != null && typeof itm.listScope === 'string') {
        itm.listScopeDescrip = await this.getListScope(itm.listScope);
      }
    }

    this.stores = [...itms];
  }

  /**
   * The function `getListScope` is an asynchronous function that takes a `listScope` parameter and
   * returns a Promise that resolves to a string.
   * @param {string} listScope - The parameter `listScope` is a string that represents the value of the
   * `fieldVal` property in the `getCategoryById` API call. It is used to specify the category ID for
   * which the data needs to be fetched.
   * @returns a Promise that resolves to a string.
   */

  async getListScope(listScope: string): Promise<string> {
    try {
      const data = await this.apiService.getCategoryById({
        fieldId: 'listScope',
        fieldVal: listScope
      });
      return data.descrip;
    } catch (error) {
      console.error("An error occurred while fetching data:", error);
      return '';
    }
  }

  /**
   * The function `getDateFormatted` returns the current date in the format "dd/MM/yyyy".
   * @returns a string representing the current date in the format "dd/MM/yyyy".
   */
  getDateFormatted(): string {
    const currentDate = new Date();
    return this.datePipe.transform(currentDate, 'dd/MM/yyyy');
  }

  /**
   * The function sets default values for certain fields in a data form based on the provided security
   * document.
   * @param {SecurityDocument} _security - The _security parameter is of type SecurityDocument.
   */
  setDefaultValuesForm(_security: SecurityDocument) {
    this.dataForm.patchValue({
      startDate: this.getDateFormatted(),
      country: this.countrySelected || null,
      scope: this.storeSelected.listScope === 'Z' ? true : false,
      currentList: this.storeSelected || 'S/N'
    });
  }
  getCountrySpkLangByCountryId() {

    this.apiService.getCountrySpkLangByCountryId({
      "language": this.security.language,
      "countryId": this.countrySelected.countryId
    }).then(
      data => {
        this.listLanguage = data;

        if (!this.selectedLanguage || !this.selectedLanguage.value) {
          this.selectedLanguage = this.listLanguage.find(item => item.valueId === this.security.language);
        }
        this.getData();

      });
  }

  obtainMonthsNames(meses: string, lenguaje: string = 'es'): string {
    const mesesArray = meses.split(',').map(m => parseInt(m.trim(), 10) - 1); // 0-based
    const nombres = mesesArray.map(mes => {
      const fecha = new Date(2000, mes); // Fecha ficticia
      return fecha.toLocaleString(lenguaje, { month: 'long' });
    });
  
    return nombres.join(', ');
  }
}
